/*
 * Marketing, Social: Facebook, Instagram and Google posts like the old Steward's Posts tab (posts.php, media.php,
 * mkPostsHTML in 15-marketing.js). A month calendar where a day starts a post and a post opens it, the month's posts as
 * cards with their photo, words and each place's logo, Schedule the month's drafts, Try again on one that did not go
 * out, and the photo library with uploads from a phone or computer. The writer shows the post as people see it in the
 * feed, the hook starters, and soft tips (hook before "more", asks for a comment, brand tags, 5 hashtags on Instagram).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  brandTags,
  HOOK_STARTERS,
  isoToWall,
  SOCIAL_FOLD,
  SOCIAL_LIMITS,
  socialPostTips,
  wallToIso,
  type MediaRow,
  type SocialPlatform,
  type SocialPostRow,
} from "@steward/shared";
import {
  useAddMedia,
  useGbp,
  useMedia,
  usePatchMedia,
  useRemoveMedia,
  useRemoveSocial,
  useRetrySocial,
  useSaveSocial,
  useScheduleMonth,
  useSocial,
  useUploadMedia,
} from "../api/marketing";
import { useSettings } from "../api/hooks";
import { useSession } from "../app/session";
import { InputText } from "../components/fields";
import { dayDate, monthName, plural, shortDate, todayIn } from "../components/format";
import { Icon } from "../components/Icon";
import { Modal, useToast } from "../components/overlays";
import { Banner, Button, Card, IconButton, LoadError, Skeleton, StatusLabel, Switch, type Status } from "../components/parts";
import { PostsCalendar } from "./PostsCalendar";

const errText = (e: unknown) => (e instanceof Error ? e.message : "That didn't work.");

function useRun() {
  const toast = useToast();
  return async (work: () => Promise<unknown>, done?: string) => {
    try {
      await work();
      if (done) toast(done);
      return true;
    } catch (e) {
      toast(errText(e), { kind: "error" });
      return false;
    }
  };
}

/** A photo from the server, read with the company header, or a plain box while it loads (and in the preview). */
export function Thumb({ fileId, alt, className = "mk-thumb" }: { fileId: string; alt: string; className?: string }) {
  const { company } = useSession();
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    void fetch(`/v1/files/${fileId}`, { credentials: "include", headers: { "x-company-id": company.id } })
      .then((r) => (r.ok && r.headers.get("content-type")?.startsWith("image/") ? r.blob() : null))
      .then((b) => {
        if (b) {
          url = URL.createObjectURL(b);
          setSrc(url);
        }
      })
      .catch(() => undefined);
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [fileId, company.id]);
  return src ? <img src={src} alt={alt} className={className} /> : <span className={className} role="img" aria-label={alt} />;
}

/** Why approved replies and posts are waiting, if they are: posting is not linked yet, or its switch is off. */
export function HeldNote() {
  const { company } = useSession();
  const g = useGbp(company.id).data;
  const m = useSettings(company.id).data?.settings.marketing;
  if (!g || (g.postingConnected && m?.postingLive)) return null;
  return (
    <Banner>
      {g.postingConnected
        ? "Posting is off, so approved replies and posts wait as held. Turn it on in Marketing settings."
        : "Posting is not linked yet, so approved replies and posts wait as held. Sign in under To set up at the top."}
    </Banner>
  );
}

const WHERE: Record<SocialPlatform, string> = { facebook: "Facebook", instagram: "Instagram", google: "Google" };
const STATUS: Record<SocialPostRow["status"], [string, Status]> = {
  draft: ["Draft", "inactive"],
  scheduled: ["Scheduled", "informative"],
  held: ["Did not go out", "warning"],
  posted: ["Posted", "success"],
};

/** The networks' own logos, in their colors (NET_LOGO in 15-marketing.js). */
function NetLogo({ p }: { p: SocialPlatform }) {
  if (p === "facebook")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="12" r="12" fill="#1877F2" />
        <path
          fill="#fff"
          d="M13.4 19.6v-6.3h2.1l.3-2.5h-2.4V9.3c0-.7.2-1.2 1.2-1.2h1.3V5.9c-.2 0-1-.1-1.9-.1-1.9 0-3.2 1.2-3.2 3.3v1.7H8.7v2.5h2.1v6.3z"
        />
      </svg>
    );
  if (p === "instagram")
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <defs>
          <linearGradient id="mk-ig" x1="0" y1="1" x2="1" y2="0">
            <stop offset="0" stopColor="#FEDA75" />
            <stop offset=".35" stopColor="#FA7E1E" />
            <stop offset=".6" stopColor="#D62976" />
            <stop offset=".85" stopColor="#962FBF" />
            <stop offset="1" stopColor="#4F5BD5" />
          </linearGradient>
        </defs>
        <rect width="24" height="24" rx="6" fill="url(#mk-ig)" />
        <rect x="6" y="6" width="12" height="12" rx="3.6" fill="none" stroke="#fff" strokeWidth="1.8" />
        <circle cx="12" cy="12" r="2.9" fill="none" stroke="#fff" strokeWidth="1.8" />
        <circle cx="16.1" cy="7.9" r="1" fill="#fff" />
      </svg>
    );
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="12" fill="#fff" />
      <path fill="#4285F4" d="M19.6 12.2c0-.6 0-1.1-.1-1.6H12v3.1h4.3a3.7 3.7 0 01-1.6 2.4v2h2.6c1.5-1.4 2.3-3.4 2.3-5.9z" />
      <path fill="#34A853" d="M12 20c2.2 0 4-.7 5.3-1.9l-2.6-2c-.7.5-1.6.8-2.7.8-2.1 0-3.8-1.4-4.5-3.3H4.8v2.1A8 8 0 0012 20z" />
      <path fill="#FBBC05" d="M7.5 13.6a4.8 4.8 0 010-3.1V8.4H4.8a8 8 0 000 7.2z" />
      <path fill="#EA4335" d="M12 7.2c1.2 0 2.2.4 3.1 1.2l2.3-2.3A8 8 0 004.8 8.4l2.7 2.1c.7-1.9 2.4-3.3 4.5-3.3z" />
    </svg>
  );
}

/** Each place a post goes, as its logo: posted, waiting (faded) or did not go out (red), with why on hover. */
function Places({ p }: { p: SocialPostRow }) {
  return (
    <span className="mk-nets">
      {p.platforms.map((x) => {
        const r = p.results[x];
        const how = r?.ok ? "ok" : r ? "bad" : "wait";
        const tip = `${WHERE[x]}. ${
          r?.ok
            ? `Posted ${shortDate(r.at.slice(0, 10))}`
            : r
              ? `Did not go out. ${r.err ?? ""}`
              : p.status === "draft"
                ? "A draft, not scheduled yet"
                : "Goes out at its time"
        }`;
        return (
          <span key={x} className={`mk-net ${how}`} data-tip={tip} aria-label={tip} tabIndex={0}>
            <NetLogo p={x} />
          </span>
        );
      })}
    </span>
  );
}

/** Reads a picked photo as base64 for the upload route. */
const readPhoto = (f: File) =>
  new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "");
    r.onerror = () => reject(new Error("That photo could not be read."));
    r.readAsDataURL(f);
  });

/** Uploads photos one at a time and says how it went. Answers the new library rows' ids, newest first. */
function usePhotoUpload() {
  const upload = useUploadMedia();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (list: File[]): Promise<string[]> => {
    const photos = list.filter((f) => ["image/jpeg", "image/png", "image/webp"].includes(f.type));
    if (!photos.length) {
      toast("Use a JPEG, PNG or WebP photo.", { kind: "error" });
      return [];
    }
    setBusy(true);
    const added: string[] = [];
    let bad = 0;
    for (const [i, f] of photos.entries()) {
      if (photos.length > 1) toast(`Adding photo ${String(i + 1)} of ${String(photos.length)}`);
      try {
        if (f.size > 10 * 1_048_576) throw new Error("That photo is over 10 MB.");
        const r = await upload.mutateAsync({ name: f.name, mime: f.type, dataBase64: await readPhoto(f), caption: "" });
        if (r.media[0]) added.push(r.media[0].id);
      } catch (e) {
        bad += 1;
        if (photos.length === 1) toast(errText(e), { kind: "error" });
      }
    }
    setBusy(false);
    if (added.length)
      toast(bad ? `${String(added.length)} added. ${String(bad)} did not save.` : plural(added.length, "photo") + " added");
    return added;
  };
  return { busy, run };
}

// The writer -----------------------------------------------------------------------------------------------------------

interface Draft {
  id: string | null;
  body: string;
  mediaIds: string[];
  platforms: SocialPlatform[];
  day: string;
  time: string;
  idea: string;
}

const blank = (day: string): Draft => ({
  id: null,
  body: "",
  mediaIds: [],
  platforms: ["facebook", "instagram", "google"],
  day,
  time: "10:00",
  idea: "",
});

/** The post as people see it in the feed: the photo, then the words cut at "more". */
function Preview({ d, media, name }: { d: Draft; media: MediaRow[]; name: string }) {
  const [all, setAll] = useState(false);
  const photo = media.find((m) => m.id === d.mediaIds[0]);
  const first = d.body.split("\n")[0] ?? "";
  const cut = d.body.length > SOCIAL_FOLD || d.body.includes("\n") ? Math.min(SOCIAL_FOLD, first.length) : d.body.length;
  const shown = all ? d.body : d.body.slice(0, cut);
  return (
    <div className="mk-preview" aria-label="How it looks in the feed">
      <div className="mk-preview-head">
        <span className="mk-preview-mark" aria-hidden>
          {name
            .replace(/^the\s+/i, "")
            .slice(0, 1)
            .toUpperCase()}
        </span>
        <b>{name}</b>
      </div>
      {photo ? (
        <Thumb fileId={photo.fileId} alt={photo.caption || "Post photo"} className="mk-preview-img" />
      ) : (
        <span className="mk-preview-img empty">No photo yet</span>
      )}
      {d.mediaIds.length > 1 ? <span className="muted small">{`1 of ${String(d.mediaIds.length)} photos`}</span> : null}
      <p className="mk-preview-text">
        {shown || <span className="muted">The words show here.</span>}
        {!all && shown.length < d.body.length ? (
          <button type="button" className="mk-more" onClick={() => setAll(true)}>
            ... more
          </button>
        ) : null}
      </p>
      <span className="muted small">{all ? "All of it." : 'What shows before "more". Put the hook here.'}</span>
    </div>
  );
}

function Writer({
  draft,
  post,
  media,
  onClose,
}: {
  draft: Draft;
  post: SocialPostRow | null;
  media: MediaRow[];
  onClose: () => void;
}) {
  const { company } = useSession();
  const [d, setD] = useState(draft);
  const save = useSaveSocial();
  const remove = useRemoveSocial();
  const retry = useRetrySocial();
  const run = useRun();
  const photos = usePhotoUpload();
  const text = useRef<HTMLTextAreaElement>(null);
  const locked = !!post && (post.status === "posted" || Object.values(post.results).some((r) => r.ok));
  const usable = media.filter((m) => !m.blocked);
  const tags = brandTags(company.name);
  const tips = socialPostTips(d.body, d.platforms, company.name);
  const limit = d.platforms.includes("google") ? SOCIAL_LIMITS.google : SOCIAL_LIMITS.instagram;
  const set = (patch: Partial<Draft>) => setD((x) => ({ ...x, ...patch }));
  const toggle = (p: SocialPlatform, on: boolean) =>
    set({ platforms: on ? [...d.platforms, p].filter((x, i, a) => a.indexOf(x) === i) : d.platforms.filter((x) => x !== p) });

  const send = (draftOnly: boolean) =>
    void run(
      async () => {
        await save.mutateAsync({
          id: d.id ?? undefined,
          body: d.body.trim(),
          mediaIds: d.mediaIds,
          platforms: d.platforms,
          scheduledFor: d.day ? wallToIso(d.day, d.time || "10:00", company.timezone) : null,
          draft: draftOnly,
        });
        onClose();
      },
      draftOnly ? "Draft saved" : `Scheduled for ${dayDate(d.day)} at ${clockWords(d.time)}`,
    );

  return (
    <Modal
      title={post ? (locked ? "Post" : "Change post") : "New post"}
      open
      onClose={onClose}
      size="large"
      footer={
        <>
          {post && !locked ? (
            <Button
              type="tertiary"
              variation="destructive"
              loading={remove.isPending}
              onClick={() =>
                void run(async () => {
                  await remove.mutateAsync(post.id);
                  onClose();
                }, "Post deleted")
              }
            >
              Delete
            </Button>
          ) : null}
          <Button type="tertiary" onClick={onClose}>
            Close
          </Button>
          {locked ? (
            <Button type="secondary" onClick={() => setD({ ...d, id: null, day: "", time: "10:00" })} disabled={!d.id}>
              Use as a new post
            </Button>
          ) : null}
          {post?.status === "held" && JSON.stringify(d) === JSON.stringify(draft) ? (
            <Button
              type="secondary"
              loading={retry.isPending}
              onClick={() =>
                void run(async () => {
                  await retry.mutateAsync(post.id);
                  onClose();
                }, "It goes out again at its time")
              }
            >
              Try again
            </Button>
          ) : null}
          {!locked || !d.id ? (
            <>
              <Button type="secondary" loading={save.isPending} onClick={() => send(true)}>
                Save as draft
              </Button>
              <Button loading={save.isPending} onClick={() => send(false)}>
                Schedule
              </Button>
            </>
          ) : null}
        </>
      }
    >
      {post && locked && d.id ? (
        <Banner type="notice">This post went out, so it can't be changed here. Use it as a new post to post it again.</Banner>
      ) : null}
      {post?.status === "held" ? (
        <Banner type="warning">
          {Object.entries(post.results)
            .filter(([, r]) => !r.ok)
            .map(([k, r]) => `${WHERE[k as SocialPlatform]}: ${r.err ?? "did not go out"}`)
            .join(" ") || "It did not go out."}
        </Banner>
      ) : null}
      <div className="mk-writer">
        <div className="stack">
          {d.idea ? (
            <div className="mk-idea">
              <b>Idea for this day</b>
              <span>{d.idea}</span>
            </div>
          ) : null}
          {!locked || !d.id ? (
            <div className="stack tight">
              <span className="label">Start with a hook</span>
              <div className="chips">
                {HOOK_STARTERS.map((h) => (
                  <button
                    key={h}
                    type="button"
                    className="chip"
                    onClick={() => {
                      set({ body: d.body.trim() ? `${h}\n${d.body}` : `${h}\n` });
                      text.current?.focus();
                    }}
                  >
                    {h}
                  </button>
                ))}
              </div>
            </div>
          ) : null}
          <label className="field wide">
            <span className="label">Words</span>
            <textarea
              ref={text}
              rows={7}
              value={d.body}
              maxLength={2400}
              disabled={locked && !!d.id}
              onChange={(e) => set({ body: e.target.value })}
              placeholder={
                "A hook or a question first.\nThen one short tip people want to save.\nAsk for a comment, tag or save."
              }
            />
          </label>
          <div className="row between wrap">
            <span className={`small ${d.body.length > limit ? "bad" : "muted"}`}>
              {`${d.body.length.toLocaleString("en-US")} of ${limit.toLocaleString("en-US")} characters`}
              {d.platforms.includes("google") ? ", the most Google takes" : ""}
            </span>
            {(!locked || !d.id) && !d.body.includes(tags.split(" ")[0]!) ? (
              <Button type="tertiary" size="small" icon="tag" onClick={() => set({ body: `${d.body.trimEnd()}\n\n${tags}` })}>
                Add {tags}
              </Button>
            ) : null}
          </div>
          {d.body.trim() ? (
            <ul className="mk-tips" aria-label="Tips">
              {tips.map((t) => (
                <li key={t.text} className={t.ok ? "ok" : "no"}>
                  <Icon name={t.ok ? "check" : "info"} size={16} />
                  {t.text}
                </li>
              ))}
            </ul>
          ) : null}
          <div className="stack tight">
            <div className="row between">
              <span className="label">Photos</span>
              {!locked || !d.id ? (
                <label className={`btn tertiary small${photos.busy ? " busy" : ""}`}>
                  <Icon name="camera" size={18} />
                  {photos.busy ? "Adding" : "Add a photo"}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    hidden
                    onChange={(e) => {
                      const list = [...(e.target.files ?? [])];
                      e.target.value = "";
                      void photos.run(list).then((ids) => set({ mediaIds: [...d.mediaIds, ...ids].slice(0, 10) }));
                    }}
                  />
                </label>
              ) : null}
            </div>
            {usable.length === 0 ? (
              <p className="muted small">No photos yet. Add one from your phone or computer, or add crew photos below.</p>
            ) : (
              <div className="mk-photos" role="group" aria-label="Photos">
                {usable.map((x) => {
                  const n = d.mediaIds.indexOf(x.id);
                  return (
                    <button
                      key={x.id}
                      type="button"
                      aria-pressed={n >= 0}
                      className="mk-pick"
                      disabled={locked && !!d.id}
                      title={`${x.caption || "Job photo"}${x.used ? `. Used in ${plural(x.used, "post")}` : ""}`}
                      onClick={() =>
                        set({ mediaIds: n >= 0 ? d.mediaIds.filter((i) => i !== x.id) : [...d.mediaIds, x.id].slice(0, 10) })
                      }
                    >
                      <Thumb fileId={x.fileId} alt={x.caption || "Job photo"} />
                      {n >= 0 ? <span className="mk-pick-n">{n + 1}</span> : null}
                      {x.used ? <span className="mk-pick-used">{`Used ${String(x.used)}x`}</span> : null}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="stack tight">
            <span className="label">Where it goes</span>
            <div className="row wrap">
              {(["facebook", "instagram", "google"] as const).map((p) => (
                <span key={p} className="row tight">
                  <Switch
                    checked={d.platforms.includes(p)}
                    disabled={locked && !!d.id}
                    onChange={(v) => toggle(p, v)}
                    label={WHERE[p]}
                  />
                  {p === "google" ? "Google profile" : WHERE[p]}
                </span>
              ))}
            </div>
            {d.platforms.includes("google") ? (
              <span className="muted small">On Google it also gets a Book button to your website.</span>
            ) : null}
            {d.platforms.includes("instagram") && d.mediaIds.length === 0 ? (
              <span className="small bad">Instagram needs a photo.</span>
            ) : null}
          </div>
          <div className="mk-two">
            <InputText
              label="Day"
              type="date"
              value={d.day}
              disabled={locked && !!d.id}
              onChange={(e) => set({ day: e.target.value })}
            />
            <InputText
              label="Time"
              type="time"
              step={300}
              value={d.time}
              disabled={locked && !!d.id}
              onChange={(e) => set({ time: e.target.value })}
            />
          </div>
          {post ? (
            <div className="stack tight">
              {post.platforms.map((x) => {
                const r = post.results[x];
                return (
                  <span key={x} className={`small ${r && !r.ok ? "bad" : "muted"}`}>
                    {`${WHERE[x]}: ${
                      r?.ok
                        ? `posted ${shortDate(r.at.slice(0, 10))}`
                        : r
                          ? `did not go out. ${r.err ?? ""}`
                          : "waiting for its time"
                    }`}
                  </span>
                );
              })}
            </div>
          ) : null}
        </div>
        <Preview d={d} media={media} name={company.name} />
      </div>
    </Modal>
  );
}

const clockWords = (hhmm: string) => {
  const [h, m] = (hhmm || "10:00").split(":").map(Number);
  return `${String(((h! + 11) % 12) + 1)}${m ? `:${String(m).padStart(2, "0")}` : ""} ${h! < 12 ? "AM" : "PM"}`;
};

// The month's posts ----------------------------------------------------------------------------------------------------

function MonthCards({ posts, media, onOpen }: { posts: SocialPostRow[]; media: MediaRow[]; onOpen: (p: SocialPostRow) => void }) {
  const { company } = useSession();
  const strip = useRef<HTMLDivElement>(null);
  const retry = useRetrySocial();
  const run = useRun();
  if (!posts.length) return null;
  const scroll = (dir: number) => strip.current?.scrollBy({ left: dir * 300, behavior: "smooth" });
  return (
    <Card
      title="This month's posts"
      action={
        <span className="row tight">
          <IconButton icon="chevronLeft" label="Earlier posts" onClick={() => scroll(-1)} />
          <IconButton icon="chevronRight" label="Later posts" onClick={() => scroll(1)} />
        </span>
      }
    >
      <div className="mk-slide" ref={strip}>
        {posts.map((p) => {
          const photo = media.find((m) => m.id === p.mediaIds[0]);
          const when = p.scheduledFor ? isoToWall(p.scheduledFor, company.timezone) : null;
          return (
            <article key={p.id} className={`mk-slide-card ${p.status}`}>
              <div className="row between">
                <span>
                  <b>{when ? shortDate(when.day) : "No day"}</b> {when ? clockWords(when.time) : ""}
                </span>
                <StatusLabel status={STATUS[p.status][1]}>{STATUS[p.status][0]}</StatusLabel>
              </div>
              {photo ? (
                <Thumb fileId={photo.fileId} alt={photo.caption || "Post photo"} className="mk-slide-img" />
              ) : (
                <span className="mk-slide-img empty">No photo</span>
              )}
              <p className="mk-slide-text">{p.body}</p>
              <Places p={p} />
              <div className="row">
                <Button type="secondary" size="small" onClick={() => onOpen(p)}>
                  Open
                </Button>
                {p.status === "held" ? (
                  <Button
                    type="tertiary"
                    size="small"
                    loading={retry.isPending}
                    onClick={() => void run(() => retry.mutateAsync(p.id), "It goes out again at its time")}
                  >
                    Try again
                  </Button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    </Card>
  );
}

// Photo library --------------------------------------------------------------------------------------------------------

function Caption({ m }: { m: MediaRow }) {
  const [v, setV] = useState(m.caption);
  const patch = usePatchMedia();
  const run = useRun();
  return (
    <input
      className="mk-caption"
      value={v}
      maxLength={140}
      placeholder="What's in it"
      aria-label="What's in this photo"
      onChange={(e) => setV(e.target.value)}
      onBlur={() => {
        if (v.trim() !== m.caption) void run(() => patch.mutateAsync({ id: m.id, caption: v.trim() }), "Saved");
      }}
    />
  );
}

function Library({ media, canEdit, children }: { media: MediaRow[]; canEdit: boolean; children: ReactNode }) {
  const patch = usePatchMedia();
  const remove = useRemoveMedia();
  const photos = usePhotoUpload();
  const run = useRun();
  const [over, setOver] = useState(false);
  return (
    <Card
      title="Photo library"
      action={
        canEdit ? (
          <span className="row tight">
            {children}
            <label className={`btn secondary small${photos.busy ? " busy" : ""}`}>
              <Icon name="plus" size={18} />
              {photos.busy ? "Adding" : "Add photos"}
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                multiple
                hidden
                onChange={(e) => {
                  const list = [...(e.target.files ?? [])];
                  e.target.value = "";
                  void photos.run(list);
                }}
              />
            </label>
          </span>
        ) : null
      }
    >
      <div
        className={`stack${over ? " mk-drop over" : ""}`}
        onDragOver={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          if (!canEdit) return;
          e.preventDefault();
          setOver(false);
          void photos.run([...e.dataTransfer.files]);
        }}
      >
        <p className="muted">
          Real photos from jobs. Before and afters, the crew at work, finished rooms and yards. Say what is in each one. Use Keep
          out on any photo that should never be posted, like a toilet.
          {canEdit ? " Drop photos here to add them." : ""}
        </p>
        {media.length === 0 ? (
          <p className="muted">No photos yet.</p>
        ) : (
          <div className="mk-photos">
            {media.map((x) => (
              <div key={x.id} className="mk-media">
                <Thumb fileId={x.fileId} alt={x.caption || "Job photo"} />
                {canEdit ? <Caption m={x} /> : <span className="small">{x.caption}</span>}
                <span className="muted small">{x.used ? `Used in ${plural(x.used, "post")}` : "Not used yet"}</span>
                {x.blocked ? <StatusLabel status="critical">Kept out</StatusLabel> : null}
                {canEdit ? (
                  <span className="row tight">
                    <Button
                      type="tertiary"
                      size="small"
                      onClick={() =>
                        void run(() =>
                          patch.mutateAsync({
                            id: x.id,
                            tags: x.blocked ? x.tags.filter((t) => t !== "toilet") : [...x.tags, "toilet"],
                          }),
                        )
                      }
                    >
                      {x.blocked ? "Allow" : "Keep out"}
                    </Button>
                    <Button
                      type="tertiary"
                      size="small"
                      variation="destructive"
                      onClick={() => void run(() => remove.mutateAsync(x.id), "Photo taken out")}
                    >
                      Delete
                    </Button>
                  </span>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
  );
}

function CrewPhotos({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { company } = useSession();
  const m = useMedia(company.id);
  const addMedia = useAddMedia();
  const run = useRun();
  const picks = m.data?.visitPhotos ?? [];
  return (
    <Modal
      title="Add crew photos"
      open={open}
      onClose={onClose}
      footer={
        <Button type="tertiary" onClick={onClose}>
          Done
        </Button>
      }
    >
      {picks.length === 0 ? (
        <p className="muted">No crew photos yet. They come from visits.</p>
      ) : (
        <div className="mk-photos">
          {picks.map((v) => (
            <div key={v.fileId} className="mk-media">
              <Thumb fileId={v.fileId} alt={`${v.client}, ${v.kind}`} />
              <span className="muted small">
                {v.client}, {shortDate(v.date)}
              </span>
              {v.inLibrary ? (
                <StatusLabel status="success">In library</StatusLabel>
              ) : (
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => void run(() => addMedia.mutateAsync({ fileIds: [v.fileId], tags: [v.kind] }), "Added")}
                >
                  Add
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}

// The tab --------------------------------------------------------------------------------------------------------------

export function Social() {
  const { company, can } = useSession();
  const m = useMedia(company.id);
  const s = useSocial(company.id);
  const schedule = useScheduleMonth();
  const run = useRun();
  const today = todayIn(company.timezone);
  const [month, setMonth] = useState(today.slice(0, 7));
  const [picking, setPicking] = useState(false);
  const [open, setOpen] = useState<{ draft: Draft; post: SocialPostRow | null; key: number } | null>(null);
  const edit = can("marketing.update");
  if (m.error) return <LoadError error={m.error} retry={() => void m.refetch()} />;
  if (s.error) return <LoadError error={s.error} retry={() => void s.refetch()} />;
  if (!m.data || !s.data) return <Skeleton rows={6} />;
  const media = m.data.media;
  const posts = s.data.posts;
  const dayOf = (p: SocialPostRow) => (p.scheduledFor ? isoToWall(p.scheduledFor, company.timezone).day : "");
  const inMonth = posts
    .filter((p) => dayOf(p).startsWith(month))
    .sort((a, b) => dayOf(a).localeCompare(dayOf(b)) || (a.scheduledFor ?? "").localeCompare(b.scheduledFor ?? ""));
  const now = Date.now();
  const readyDrafts = inMonth.filter((p) => p.status === "draft" && p.scheduledFor && Date.parse(p.scheduledFor) > now).length;
  const startNew = (day: string, idea: string) => setOpen({ draft: { ...blank(day), idea }, post: null, key: Date.now() });
  const openPost = (p: SocialPostRow) => {
    const w = p.scheduledFor ? isoToWall(p.scheduledFor, company.timezone) : { day: "", time: "10:00" };
    setOpen({
      draft: { id: p.id, body: p.body, mediaIds: p.mediaIds, platforms: p.platforms, day: w.day, time: w.time, idea: "" },
      post: p,
      key: Date.now(),
    });
  };
  const upcoming = posts.filter((p) => p.status === "scheduled").length;
  const held = posts.filter((p) => p.status === "held").length;
  return (
    <div className="stack">
      <HeldNote />
      <div className="mk-social-top">
        <span className="muted">
          {upcoming
            ? `${plural(upcoming, "post")} scheduled.`
            : "Nothing is scheduled. Two or three posts a week keep the brand in front of people who know you."}
          {held ? ` ${plural(held, "post")} did not go out.` : ""}
        </span>
        {edit ? (
          <Button icon="plus" onClick={() => startNew(today, "")}>
            New post
          </Button>
        ) : null}
      </div>
      <PostsCalendar
        posts={posts}
        month={month}
        onMonth={setMonth}
        onStart={edit ? startNew : undefined}
        onOpen={openPost}
        footer={
          edit && readyDrafts ? (
            <div className="row wrap">
              <Button
                loading={schedule.isPending}
                onClick={() =>
                  void run(async () => {
                    const r = await schedule.mutateAsync(month);
                    if (r.scheduled === 0) throw new Error("None of them are ready. Open each draft to see what it needs.");
                  }, "Drafts scheduled")
                }
              >
                {`Schedule the ${plural(readyDrafts, "draft")} in ${monthName(`${month}-01`)}`}
              </Button>
              <span className="muted small">Look them over first. Each goes out at its own day and time.</span>
            </div>
          ) : null
        }
      />
      <MonthCards posts={inMonth} media={media} onOpen={openPost} />
      <Library media={media} canEdit={edit}>
        <Button type="tertiary" size="small" icon="camera" onClick={() => setPicking(true)}>
          Crew photos
        </Button>
      </Library>
      <CrewPhotos open={picking} onClose={() => setPicking(false)} />
      {open ? <Writer key={open.key} draft={open.draft} post={open.post} media={media} onClose={() => setOpen(null)} /> : null}
    </div>
  );
}
